import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import './App.css'

// CONFIGURATION: Set your date here (Year, Month - 1, Day, Hour, Minute)
const TARGET_DATE = new Date(2026, 1, 7, 10, 30, 0); // Feb 7, 2026, 10:30 AM

const ValentineInvite = () => {
  const [stage, setStage] = useState(1);
  const [noButtonPos, setNoButtonPos] = useState({ x: 0, y: 0 });
  const [noAttempts, setNoAttempts] = useState(0);
  const [trail, setTrail] = useState([]);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  // 1. Live Countdown Timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const diff = TARGET_DATE - now;
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          mins: Math.floor((diff / 1000 / 60) % 60),
          secs: Math.floor((diff / 1000) % 60),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 2. Premium Particle Cursor Trail
  useEffect(() => {
    const handleMouseMove = (e) => {
      const newParticle = {
        x: e.clientX,
        y: e.clientY,
        id: Math.random(),
        vx: (Math.random() - 0.5) * 3, // Drift velocity X
        vy: (Math.random() - 0.5) * 3, // Drift velocity Y
        rotation: Math.random() * 360,
        size: Math.random() * 1.5 + 0.5,
      };
      setTrail((prev) => [...prev, newParticle].slice(-25));
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // 3. Smart Repulsion Logic for "No" Button
  const fleeCursor = useCallback((e) => {
    setNoAttempts(prev => prev + 1);
    const safeZone = 200; // Radius button keeps away from cursor

    const getRandomSpot = () => {
      const x = Math.random() * (window.innerWidth - 120);
      const y = Math.random() * (window.innerHeight - 50);
      const dist = Math.hypot(x - e.clientX, y - e.clientY);
      return dist < safeZone ? getRandomSpot() : { x, y };
    };

    const spot = getRandomSpot();
    // Convert absolute screen position to relative Framer coordinates
    setNoButtonPos({
      x: spot.x - (window.innerWidth / 2) + 60,
      y: spot.y - (window.innerHeight / 2) + 160
    });
  }, []);

  const celebrate = () => {
    setStage(3);
    const end = Date.now() + 5000;
    const colors = ['#fb7185', '#ff1493', '#ff69b4'];
    (function frame() {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    }());
  };

  return (
    <div className="val-container">
      {/* Background Decor */}
      <div className="bg-floating">
        {[...Array(15)].map((_, i) => (
          <div key={i} className="heart-bg" style={{ left: `${i * 7}%`, animationDelay: `${i * 0.4}s` }}>❤️</div>
        ))}
      </div>

      {/* Premium Trail Particles */}
      {trail.map((p, i) => (
        <motion.span
          key={p.id}
          className="trail-particle"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 0], scale: [0, p.size, 0], x: p.vx * 30, y: p.vy * 30 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          style={{ left: p.x, top: p.y, rotate: p.rotation }}
        >
          ❤️
        </motion.span>
      ))}

      <AnimatePresence mode="wait">
        {stage === 1 && (
          <motion.div
            key="stage1" exit={{ y: -500, opacity: 0, transition: { duration: 0.8 } }}
            onClick={() => setStage(2)} className="envelope-trigger"
          >
            <div className="env-visual">💌</div>
            <p className="hint-text">A special invite just for you...</p>
          </motion.div>
        )}

        {stage === 2 && (
          <motion.div
            key="stage2" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="romantic-card"
          >
            <span className="card-rose">🌹</span>
            <h1 className="card-title">Will you be my Valentine?</h1>
            <p className="card-msg">I know we're celebrating it a week early, but I promise it'll be an amazing day.</p>
            <p className="card-warning">(Warning: It is quite difficult to say no...)</p>

            <div className="btn-row">
              <button className="btn-yes" onClick={celebrate}>I'd love to!</button>
              <motion.button
                className="btn-no"
                onMouseEnter={fleeCursor}
                animate={noAttempts > 0 ? { x: noButtonPos.x, y: noButtonPos.y } : {}}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                {noAttempts > 5 ? "Nice try! 😂" : "No"}
              </motion.button>
            </div>
          </motion.div>
        )}

        {stage === 3 && (
          <motion.div
            key="stage3" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="romantic-card success"
          >
            <h1 className="card-title">Best. Decision. Ever. 🥰</h1>
            <p className="card-msg">Counting down the seconds until our date!</p>

            <div className="timer-wrap">
              <div className="t-block"><span>{timeLeft.days}</span><label>Days</label></div>
              <div className="t-block"><span>{timeLeft.hours}</span><label>Hrs</label></div>
              <div className="t-block"><span>{timeLeft.mins}</span><label>Mins</label></div>
              <div className="t-block"><span>{timeLeft.secs}</span><label>Secs</label></div>
            </div>

            <div className="celebration-footer">🥂🍓✨</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ValentineInvite;
